<template>
  <v-app-bar class="tunnl-header" elevation="0">
    <div v-if="!loggedIn" style="width: 256px">
      <logo />
    </div>
    <breadcrumbs />
    <v-spacer />
    <v-tooltip v-if="showTrialIndicator" location="bottom" interactive>
      <template #activator="{ props: activatorProps }">
        <v-chip v-bind="activatorProps" variant="tonal" color="primary">
          Trial · {{ trialDaysLeft }} days left
        </v-chip>
      </template>
      Ready to subscribe?
      <a
        class="trial-tooltip-link"
        :href="getLink('pricing')"
        target="_blank"
        rel="noopener noreferrer">
        Click here
      </a>
      to reach out to {{ themeBrand }} Sales.
    </v-tooltip>
    <v-btn
      v-if="showUpgradeBtn"
      variant="tonal"
      color="primary"
      class="mr-4"
      target="_blank"
      :href="getLink('pricing')"
      @click="trackActivity">
      upgrade
    </v-btn>
    <notifications v-if="loggedIn && !impersonating.isImpersonating" />
    <user-menu
      :impersonating="impersonating.isImpersonating"
      @stop-impersonating="stopImpersonating" />

    <header-alert
      v-if="impersonating.isImpersonating"
      button-label="Logout"
      :loading="impersonating.loggingOut"
      @dismiss="stopImpersonating">
      {{ impersonatingMessage }}
    </header-alert>

    <header-alert
      v-if="showTechIssuesAlert"
      button-label="Dismiss"
      @dismiss="showTechIssuesAlert = false">
      The {{ themeBrand }} App is experiencing some intermittent technical
      issues. We are working to resolve these and apologize for the
      inconvenience.
    </header-alert>

    <header-alert
      v-if="upgradeNotification"
      button-label="Dismiss"
      @dismiss="clearUpgradeNotification">
      <span v-html="upgradeNotification.message" />
    </header-alert>
  </v-app-bar>
</template>

<script>
import Cookies from "js-cookie";
import { mapStores } from "pinia";
import { useAppStateStore } from "@/stores/AppStateStore";
import { useCampaignEffectivenessStore } from "@/stores/CampaignEffectivenessStore";
import { useNotificationStore } from "@/stores/NotificationStore";
import { useResearchStudioStore } from "@/stores/ResearchStudioStore";
import { useUserActivityStore } from "@/stores/UserActivityStore";
import { useUserStore } from "@/stores/UserStore";
import { getLink } from "@/helpers/links";
import { userTierMixin } from "@/mixins/UserTier";
import Breadcrumbs from "@/layouts/Breadcrumbs.vue";
import HeaderAlert from "@/layouts/HeaderAlert.vue";
import Logo from "@/layouts/Logo.vue";
import Notifications from "@/layouts/Notifications.vue";
import UserMenu from "@/layouts/UserMenu.vue";

export default {
  name: "PlatformHeader",
  components: {
    Breadcrumbs,
    HeaderAlert,
    Logo,
    Notifications,
    UserMenu,
  },
  mixins: [userTierMixin],
  data: () => ({
    showTechIssuesAlert: false,
    trialCountdownNow: Date.now(),
    trialCountdownTimeoutId: null,
    impersonating: {
      isImpersonating: false,
      loggingOut: false,
      name: undefined,
      email: undefined,
    },
  }),
  computed: {
    ...mapStores(
      useAppStateStore,
      useCampaignEffectivenessStore,
      useNotificationStore,
      useResearchStudioStore,
      useUserActivityStore,
      useUserStore,
    ),
    trialExpiresAt() {
      const raw = this.currentAppClient?.access_expires_at;
      if (!raw) return null;
      const expiresAt = new Date(raw);
      return Number.isNaN(expiresAt.getTime()) ? null : expiresAt;
    },
    showTrialIndicator() {
      return (
        this.currentAppClient?.app_pricing_tier?.name === "Trial" &&
        this.trialExpiresAt !== null
      );
    },
    trialDaysLeft() {
      const expiresAt = this.trialExpiresAt;
      if (!expiresAt) return null;
      const diffTime = expiresAt.getTime() - this.trialCountdownNow;
      return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    },
    showUpgradeBtn() {
      if (this.showTrialIndicator) return false;
      const allPaidProducts = Object.values(this.paidProductsPermissions).every(
        (p) => p,
      );
      return this.currentUser && !allPaidProducts;
    },
    impersonatingMessage() {
      return `Logged in as ${this.impersonating.name} (${this.impersonating.email})`;
    },
    upgradeNotification() {
      return this.notificationStore.notifications.find(
        (n) => n.subject === "header_alert" && n.read === false,
      );
    },
  },
  watch: {
    currentUser: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal?.id !== oldVal?.id) {
          this.setImpersonating();
        }
      },
    },
  },
  mounted() {
    this.setImpersonating();
    this.showTechIssuesAlert = this.currentUser?.roles?.every(
      (r) => r.technical_issues,
    );
    this.startTrialCountdownClock();
  },
  beforeUnmount() {
    this.stopTrialCountdownClock();
  },
  methods: {
    getLink,
    refreshTrialCountdownNow() {
      this.trialCountdownNow = Date.now();
    },
    startTrialCountdownClock() {
      this.refreshTrialCountdownNow();
      this.scheduleTrialCountdownMidnight();
      document.addEventListener(
        "visibilitychange",
        this.onTrialCountdownVisibilityChange,
      );
    },
    stopTrialCountdownClock() {
      if (this.trialCountdownTimeoutId != null) {
        clearTimeout(this.trialCountdownTimeoutId);
        this.trialCountdownTimeoutId = null;
      }
      document.removeEventListener(
        "visibilitychange",
        this.onTrialCountdownVisibilityChange,
      );
    },
    scheduleTrialCountdownMidnight() {
      if (this.trialCountdownTimeoutId != null) {
        clearTimeout(this.trialCountdownTimeoutId);
      }
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setDate(now.getDate() + 1);
      nextMidnight.setHours(0, 0, 0, 0);
      this.trialCountdownTimeoutId = setTimeout(() => {
        this.trialCountdownTimeoutId = null;
        this.refreshTrialCountdownNow();
        this.scheduleTrialCountdownMidnight();
      }, nextMidnight.getTime() - now.getTime());
    },
    onTrialCountdownVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      this.refreshTrialCountdownNow();
      this.scheduleTrialCountdownMidnight();
    },
    trackActivity() {
      this.userActivityStore.create({
        event: "upgrade attempt",
        meta_data: {
          location: "upgrade button in header",
        },
      });
    },
    clearUpgradeNotification() {
      this.notificationStore.updateNotification({
        ...this.upgradeNotification,
        read: true,
      });
      let notification = this.notificationStore.notifications.find(
        (n) => n.id === this.upgradeNotification.id,
      );
      if (notification) {
        notification = Object.assign(notification, { read: true });
      }
    },
    setImpersonating() {
      const impersonating = Cookies.get("impersonating");
      this.impersonating.isImpersonating =
        (!!impersonating &&
          impersonating === this.currentUser?.id.toString()) ||
        this.impersonating.loggingOut;
      if (!this.impersonating.loggingOut) {
        this.impersonating.name = this.currentUser?.name;
        this.impersonating.email = this.currentUser?.email;
      }
    },
    stopImpersonating() {
      this.impersonating.loggingOut = true;
      this.userStore.setCurrentOrg(undefined);
      this.userStore.setCurrentAppClient(undefined);
      this.userStore.setImpersonating(undefined);
      this.appStateStore.breadcrumbs = [];

      Promise.all([
        this.metaAudienceStore.getMetaAudiences(),
        this.campaignEffectivenessStore.listCampaigns(),
        this.researchStudioStore.listSurveys(),
      ]).then(() => {
        this.impersonating = {
          isImpersonating: false,
          loggingOut: false,
          name: undefined,
          email: undefined,
        };
        this.$router
          .push({ name: "home", query: { _r: Date.now() } })
          .catch(() => {});
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.tunnl-header {
  z-index: 9999;
  .v-breadcrumbs {
    font-family: Barlow, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.1px;
  }
}
.trial-tooltip-link {
  color: rgb(var(--v-theme-anchor));
  font-weight: 600;
  text-decoration: none;
}
</style>
